import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import CourseFormDialog from "@/components/CourseFormDialog";
import AreaFormDialog from "@/components/AreaFormDialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, BookOpen, Palette, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { YEAR_LABEL, courseYears, yearsForTrack } from "@/lib/school";
import { useAuth, isAdminOrSegreteria } from "@/lib/auth";

export default function Courses({ track }) {
  const { user } = useAuth();
  const canManage = isAdminOrSegreteria(user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [delCourse, setDelCourse] = useState(null);
  const [areasOpen, setAreasOpen] = useState(false);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["courses", { search, yearFilter, areaFilter, track }],
    queryFn: async () => {
      const params = { is_seminar: false };
      if (track) params.track_type = track;
      if (search) params.search = search;
      if (yearFilter !== "all") params.year_level = yearFilter;
      if (areaFilter !== "all") params.area_id = areaFilter;
      return (await api.get("/courses", { params })).data;
    },
  });

  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/areas")).data,
  });

  const { data: stats } = useQuery({
    queryKey: ["courses-stats", track],
    queryFn: async () => (await api.get("/courses-stats/overview", { params: track ? { track_type: track } : {} })).data,
  });
  const years = yearsForTrack(track);
  const base = track === "modulare" ? "/modulare/corsi" : "/scuola/corsi";

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/courses", p)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["courses-stats"] });
      toast.success("Corso creato");
      setCreateOpen(false);
      navigate(`${base}/${d.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const editMut = useMutation({
    mutationFn: async ({ id, p }) => (await api.patch(`/courses/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      toast.success("Corso aggiornato");
      setEditCourse(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/courses/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["courses-stats"] });
      toast.success("Corso eliminato");
      setDelCourse(null);
    },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelCourse(null); },
  });

  const groupedByYear = useMemo(() => {
    const g = { nd: [] };
    for (const c of courses) {
      const ys = courseYears(c);
      (ys.length ? ys : ["nd"]).forEach((y) => { g[y] = [...(g[y] || []), c]; });
    }
    return g;
  }, [courses]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={track === "modulare" ? "Corsi — Modulare" : "Corsi"}
        subtitle={track === "modulare" ? "Catalogo corsi del percorso modulare per anno, con CFU, docenti e moduli." : "Catalogo corsi del piano residenziale con annualità, CFU, docenti e moduli."}
        actions={canManage && (
          <div className="flex gap-2">
            <Sheet open={areasOpen} onOpenChange={setAreasOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="manage-areas-btn"
                  className="rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white transition-colors"
                >
                  <Palette size={14} className="mr-1.5"/> Aree disciplinari
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white">
                <SheetHeader>
                  <SheetTitle>Aree disciplinari</SheetTitle>
                  <SheetDescription>Organizza i corsi per disciplina con colore e coordinatore.</SheetDescription>
                </SheetHeader>
                <AreasManager />
              </SheetContent>
            </Sheet>
            <Button
              data-testid="course-add-btn"
              onClick={() => setCreateOpen(true)}
              className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
            >
              <Plus size={16} className="mr-1.5"/> Nuovo corso
            </Button>
          </div>
        )}
      />

      <div className={`grid grid-cols-2 gap-4 mb-6 ${track === "modulare" ? "md:grid-cols-4 lg:grid-cols-8" : "md:grid-cols-4"}`}>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><BookOpen size={12}/> Corsi totali</div>
          <div className="text-2xl font-semibold mt-1" data-testid="courses-total">{stats?.total || 0}</div>
        </Card>
        {years.map((y) => <YearStatCard key={y} label={YEAR_LABEL[y]} data={stats?.by_year?.[String(y)]} />)}
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="course-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca corso o codice…"
              className="pl-9"
            />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-40" data-testid="course-year-filter"><SelectValue placeholder="Anno"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{YEAR_LABEL[y]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={areaFilter} onValueChange={setAreaFilter}>
            <SelectTrigger className="w-56" data-testid="course-area-filter"><SelectValue placeholder="Area"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aree</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ background: a.color }}/>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
          ) : courses.length === 0 ? (
            <div className="py-14 text-center text-stone-400 text-sm">
              Nessun corso. Inizia creando le aree disciplinari poi il primo corso.
            </div>
          ) : (
            <div className="space-y-8">
              {[...years, "nd"].map((y) => {
                const list = groupedByYear[y] || [];
                if (list.length === 0) return null;
                return (
                  <div key={y}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-[#1C352D] text-white text-[10px] rounded-full">{y === "nd" ? "Anno non specificato" : YEAR_LABEL[y]}</Badge>
                      <span className="text-xs text-stone-500">{list.length} corsi · {list.reduce((s, c) => s + (c.cfu || 0), 0)} CFU</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {list.map((c, i) => (
                        <Card
                          key={c.id}
                          data-testid={`course-card-${c.id}`}
                          className="p-4 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors animate-in-row"
                          style={{ animationDelay: `${i * 15}ms` }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-1 h-14 rounded-full shrink-0" style={{ background: c.area_color || "#1C352D" }}/>
                            <div className="min-w-0 flex-1">
                              <Link to={`${base}/${c.id}`} className="font-medium text-[#1C352D] hover:underline block">
                                {c.name}
                              </Link>
                              <div className="text-[11px] text-stone-500 mt-0.5">{c.area_name || "—"} · {c.code || "—"}</div>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {c.cfu != null && <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">{c.cfu} CFU</Badge>}
                                {c.hours != null && <Badge className="bg-stone-100 text-stone-700 text-[10px] rounded-full">{c.hours}h</Badge>}
                                {c.modules_count > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-stone-500">
                                    <Layers size={10}/> {c.modules_count} moduli
                                  </span>
                                )}
                              </div>
                              {c.module_names?.length > 0 && (
                                <ol className="mt-2 space-y-0.5 text-[11px] text-stone-600 border-l border-stone-200 pl-2" data-testid={`course-modules-preview-${c.id}`}>
                                  {c.module_names.map((n, j) => (
                                    <li key={j} className="truncate" title={n}><span className="font-mono text-stone-400 mr-1">{j + 1}.</span>{n}</li>
                                  ))}
                                </ol>
                              )}
                              {c.teachers?.length > 0 && (
                                <div className="text-[11px] text-stone-600 mt-2 truncate">
                                  {c.teachers.map((t) => t.name).join(", ")}
                                </div>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]" data-testid={`course-actions-${c.id}`}>
                                  <MoreHorizontal size={16}/>
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40 bg-white">
                                <DropdownMenuItem onClick={() => navigate(`${base}/${c.id}`)}>Apri corso</DropdownMenuItem>
                                {canManage && <DropdownMenuItem onClick={() => setEditCourse(c)}><Pencil size={14} className="mr-2"/>Modifica</DropdownMenuItem>}
                                {canManage && <DropdownMenuSeparator/>}
                                {canManage && <DropdownMenuItem className="text-red-700 focus:text-red-800 focus:bg-red-50" onClick={() => setDelCourse(c)}>
                                  <Trash2 size={14} className="mr-2"/>Elimina
                                </DropdownMenuItem>}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      <CourseFormDialog
        open={createOpen} onOpenChange={setCreateOpen} mode="create" defaultTrack={track}
        submitting={createMut.isPending}
        onSubmit={(p) => createMut.mutate(p)}
      />
      <CourseFormDialog
        open={!!editCourse} onOpenChange={(v) => { if (!v) setEditCourse(null); }}
        mode="edit" initial={editCourse}
        submitting={editMut.isPending}
        onSubmit={(p) => editMut.mutate({ id: editCourse.id, p })}
      />

      <AlertDialog open={!!delCourse} onOpenChange={(v) => { if (!v) setDelCourse(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {delCourse?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Anche i moduli associati verranno rimossi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => delCourse && delMut.mutate(delCourse.id)}
              data-testid="course-delete-confirm"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function YearStatCard({ label, data }) {
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{data?.count || 0}<span className="text-sm text-stone-500 font-normal ml-2">corsi</span></div>
      <div className="text-xs text-stone-500 mt-0.5">{data?.cfu || 0} CFU totali</div>
    </Card>
  );
}

function AreasManager() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editArea, setEditArea] = useState(null);
  const [delArea, setDelArea] = useState(null);

  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/areas")).data,
  });

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/areas", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["areas"] }); toast.success("Area creata"); setCreateOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const editMut = useMutation({
    mutationFn: async ({ id, p }) => (await api.patch(`/areas/${id}`, p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["areas"] }); toast.success("Area aggiornata"); setEditArea(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/areas/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["areas"] }); toast.success("Area eliminata"); setDelArea(null); },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelArea(null); },
  });

  return (
    <div className="mt-4">
      <Button
        onClick={() => setCreateOpen(true)}
        className="w-full rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
        data-testid="area-add-btn"
      >
        <Plus size={14} className="mr-1.5"/> Nuova area disciplinare
      </Button>
      <ul className="mt-6 space-y-2">
        {areas.length === 0 ? (
          <li className="text-sm text-stone-500 py-6 text-center">Nessuna area. Creane una per iniziare.</li>
        ) : areas.map((a) => (
          <li key={a.id} data-testid={`area-item-${a.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:border-stone-200 transition-colors">
            <div className="w-3 h-8 rounded-full" style={{ background: a.color }}/>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm">{a.name}</div>
              <div className="text-[11px] text-stone-500">
                {a.coordinator_name || "—"} · {a.courses_count || 0} corsi
              </div>
            </div>
            <button onClick={() => setEditArea(a)} className="p-1.5 rounded hover:bg-stone-100 text-stone-500" data-testid={`area-edit-${a.id}`}><Pencil size={14}/></button>
            <button onClick={() => setDelArea(a)} className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700" data-testid={`area-del-${a.id}`}><Trash2 size={14}/></button>
          </li>
        ))}
      </ul>

      <AreaFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create"
        submitting={createMut.isPending} onSubmit={(p) => createMut.mutate(p)} />
      <AreaFormDialog open={!!editArea} onOpenChange={(v) => !v && setEditArea(null)} mode="edit" initial={editArea}
        submitting={editMut.isPending} onSubmit={(p) => editMut.mutate({ id: editArea.id, p })} />

      <AlertDialog open={!!delArea} onOpenChange={(v) => !v && setDelArea(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {delArea?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Non si può eliminare un&apos;area con corsi collegati.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => delArea && delMut.mutate(delArea.id)}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
